import { useAppDispatch, useAppSelector } from '../../../../../../store/hooks'
import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { selectAccountId } from '../../../../../../store/auth/authSlice'

import { useLazyGetFeaturesQuery } from '../../../../../../store/ai/ai'
import { useLazyGetAllScorecardsQuery, useLazyGetScorecardResultsByFilterQuery } from '../../../../../../store/ai/scorecard'
import { useLazyListRoomEventsQuery } from '../../../../../../store/media/media'
import {
  selectClientProfileAiFeatures,
  selectClientProfileEventsForPolling,
  selectClientProfileResults,
  setClientProfileAiFeatures,
  setClientProfileAiFeaturesLoading,
  setClientProfileEventsForPolling,
  setClientProfileEventsForPollingLoading,
  setClientProfileResults,
  setClientProfileScorecards,
  setClientProfileScorecardsLoading,
} from '../../../../../../store/meetingSummary/clientProfile'

export const ClientProfileInitialization = () => {
  const { roomId = '', sessionId = '' } = useParams()
  const accountId = useAppSelector(selectAccountId)
  useInitializeClientProfile(accountId, roomId, sessionId)
  return null
}

export const ScorecardInitialization = () => {
  const accountId = useAppSelector(selectAccountId)
  useInitializeClientProfileAiFeatures(accountId, '', '')
  useInitializeClientProfileScorecards(accountId)
  return null
}

const useInitializeClientProfile = (accountId: string, roomId: string, sessionId: string) => {
  useInitializeClientProfileAiFeatures(accountId, roomId, sessionId)
  useInitializeClientProfileEventsForPolling(accountId, roomId, sessionId)
  useInitializeClientProfileScorecards(accountId)
  useInitializeClientProfileResults(accountId, roomId, sessionId)
}

const useInitializeClientProfileAiFeatures = (accountId: string, roomId: string, sessionId: string) => {
  const dispatch = useAppDispatch()

  const clientProfileAiFeatures = useAppSelector(selectClientProfileAiFeatures)

  const [triggerGetAiFeatures] = useLazyGetFeaturesQuery()

  useEffect(() => {
    const fetchClientProfileAiFeatures = async () => {
      if (clientProfileAiFeatures.length) {
        return
      }
      dispatch(setClientProfileAiFeaturesLoading(true))
      try {
        const { data: clientProfileAiFeatures } = await triggerGetAiFeatures(
          {
            accountId,
            pageRequest: { page: 0, size: 500 },
            includeRestricted: true,
            type: 'scorecard',
          },
          false,
        ).unwrap()
        dispatch(setClientProfileAiFeatures(clientProfileAiFeatures))
      } finally {
        dispatch(setClientProfileAiFeaturesLoading(false))
      }
    }
    fetchClientProfileAiFeatures()
  }, [dispatch, accountId, roomId, sessionId, triggerGetAiFeatures, clientProfileAiFeatures.length])
}

const useInitializeClientProfileEventsForPolling = (accountId: string, roomId: string, sessionId: string) => {
  const dispatch = useAppDispatch()

  const eventsForPolling = useAppSelector(selectClientProfileEventsForPolling)

  const [triggerGetRoomEvents] = useLazyListRoomEventsQuery()

  useEffect(() => {
    const fetchMeetingSummaryEvents = async () => {
      if (eventsForPolling.length) {
        return
      }
      dispatch(setClientProfileEventsForPollingLoading(true))
      try {
        const { data: roomEvents } = await triggerGetRoomEvents(
          {
            accountId,
            roomId,
            sessionId,
            pageRequest: { page: 0, size: 500 },
            types: ['aiFeatureExecutionFinished', 'userAllocationUpdated'],
          },
          true,
        )
        if (!roomEvents?.data || !roomEvents.data.length) {
          return
        }
        dispatch(setClientProfileEventsForPolling(roomEvents.data))
      } catch (e: unknown) {
        console.error(e)
      } finally {
        dispatch(setClientProfileEventsForPollingLoading(false))
      }
    }
    fetchMeetingSummaryEvents()
  }, [dispatch, accountId, roomId, sessionId, triggerGetRoomEvents, eventsForPolling])
}

const useInitializeClientProfileScorecards = (accountId: string) => {
  const dispatch = useAppDispatch()
  const clientProfileAiFeatures = useAppSelector(selectClientProfileAiFeatures)
  const [triggerGetScorecards] = useLazyGetAllScorecardsQuery()

  useEffect(() => {
    if (!clientProfileAiFeatures.length) {
      return
    }
    const fetchClientProfileScoreCards = async () => {
      const aiFeatureIds = clientProfileAiFeatures.map((aiFeature) => aiFeature.id)
      dispatch(setClientProfileScorecardsLoading(true))

      try {
        const { data: scorecardsData } = await triggerGetScorecards(
          {
            accountId,
            aiFeatureIds: aiFeatureIds.join(','),
          },
          false,
        ).unwrap()
        dispatch(setClientProfileScorecards(scorecardsData))
      } catch (e: unknown) {
        console.error(e)
      } finally {
        dispatch(setClientProfileScorecardsLoading(false))
      }
    }
    fetchClientProfileScoreCards()
  }, [dispatch, accountId, clientProfileAiFeatures, triggerGetScorecards])
}

const useInitializeClientProfileResults = (accountId: string, roomId: string, sessionId: string) => {
  const dispatch = useAppDispatch()

  const clientProfileResults = useAppSelector(selectClientProfileResults)

  const [triggerGetScorecardResults] = useLazyGetScorecardResultsByFilterQuery()

  useEffect(() => {
    const getClientProfileResults = async () => {
      if (clientProfileResults.length) {
        return
      }
      try {
        const clientProfileResults = await triggerGetScorecardResults(
          {
            accountId,
            body: {
              source: {
                type: 'meeting',
                roomId: roomId,
                sessionId: sessionId,
              },
            },
          },
          false,
        ).unwrap()
        dispatch(setClientProfileResults(clientProfileResults))
      } catch (e: unknown) {
        console.error(e)
      }
    }
    getClientProfileResults()
  }, [dispatch, accountId, roomId, sessionId, triggerGetScorecardResults, clientProfileResults.length])
}
